import React, { useState } from 'react';
import { MenuBar, TabContent, Tabs } from '@brighterion/br-ui';
import { getRulesFormatForView } from 'Utils';
import PageHeader from 'Components/PageHeader';
import RouteLoading from 'Components/RouteLoading';
import errorController from 'Components/ErrorScreens';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    getUser,
    shouldShowManageTenants,
    shouldShowMerchants,
    shouldShowTenantGroups,
    shouldShowAdvancedRules
} from 'Selectors';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import useGetListNames from 'Hooks/query-hooks/list/useGetListNames';
import TenantGroupsTable from 'Routes/ManageTenants/components/TenantGroupsTable';
import MerchantOverridesTable from 'Routes/Overrides/components/MerchantsOverridesTable/MerchantsOverridesTable';
import useSingleDeployment from 'Hooks/query-hooks/deployment/useSingleDeployment';
import useGetRuleColumns, { Args } from 'Hooks/useGetRuleColumns';
import useRuleView from 'Hooks/useRuleView';
import useGetMerchantOverridesColumns from 'Hooks/useGetMerchantOverridesColumns';
import useDeployedGlobalRules from 'Hooks/query-hooks/global-rules/useDeployedGlobalRules';
import useDeployedMerchantOverrides from 'Hooks/query-hooks/merchants/useDeployedMerchantOverrides';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import { GLOBAL_RULE_TABLE_VIEW } from 'Common/enums';
import useGetTenantGroupColumns from 'Hooks/useGetTenantGroupColumns';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import GlobalRulesTable from 'Components/GlobalRulesTable';
import useGetParameters from 'Hooks/query-hooks/parameters/useGetParameters';
import GlobalRulesOverview from 'Components/GlobalRulesOverview';
import RevisionOverview from './components/RevisionOverview';
import SingleMerchantView from './components/SingleMerchantView';
import SingleTenantGroupView from './components/SingleTenantGroupView';

import styles from './RevisionWindow.css';

const RevisionWindow = () => {
    const { t } = useTranslation();
    const { users } = useGetReduxUsers();
    const { viewOption, setViewOption } = useRuleView();
    const user = useSelector(getUser);
    const { version }: { version: string } = useParams();
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const [showSingleMerchant, setShowSingleMerchant] = useState(false);
    const [currMerchant, setCurrMerchant] = useState('');
    const [showSingleTenantGroup, setShowSingleTenantGroup] = useState(false);
    const [currTenantGroup, setCurrTenantGroup] = useState<TenantGroup>({});
    const showMerchants = shouldShowMerchants();
    const isGlobalRuleSelectOption = viewOption === GLOBAL_RULE_TABLE_VIEW;
    const showTenantGroups = shouldShowManageTenants(user?.landlordId, user.tenantKey) && shouldShowTenantGroups();
    const { data: landlordInfo } = useGetLandlordInfo(showTenantGroups);

    const globalColumns: Args['columns'] = [
        'modifiedFlag',
        'virtualRuleGroup',
        'ruleId',
        'inputDescription',
        'conclusion',
        'ruleType',
        'lastModified',
        'status'
    ];

    if (landlordInfo?.isLandlord) {
        globalColumns.push('allowOverrides');
        globalColumns.push('hiddenFromTenant');
    }
    const executionColumns: Args['columns'] = [
        'modifiedFlag',
        'order',
        'ruleId',
        'inputDescription',
        'ruleGroup',
        'conclusion',
        'ruleType'
    ];
    const revisionWindowColumns = isGlobalRuleSelectOption ? globalColumns : executionColumns;

    /** Hide columns if Feature Flags are turned off */
    let filteredRulesColumns = revisionWindowColumns;
    if (!shouldShowAdvancedRules()) {
        filteredRulesColumns = revisionWindowColumns.filter((columnName) => columnName !== 'ruleType');
    }

    const rulesColumnNames = useGetRuleColumns({
        columns: filteredRulesColumns
    });

    const merchantColumnNames = useGetMerchantOverridesColumns({
        columns: [
            'modifiedFlag',
            'merchantIdNoLink',
            'merchantName',
            'merchantDescription',
            'overallThreshold',
            'activeRules',
            'overrides',
            'lastModified',
            'status'
        ]
    });

    const columns = useGetTenantGroupColumns({});

    const singleDeploymentAPI = useSingleDeployment(Number(version), users);

    const parametersAPI = useGetParameters({
        queryParams: {
            status: JSON.stringify(['active'])
        }
    });

    const listNamesAPI = useGetListNames();
    const deployedGlobalRulesAPI = useDeployedGlobalRules({
        version: Number(version),
        enabled:
            !listNamesAPI.isLoading &&
            !listNamesAPI.isFetching &&
            !parametersAPI.isLoading &&
            !parametersAPI.isFetching,
        listNameMap: listNamesAPI.data,
        showDeletedRule: true,
        parameterData: parametersAPI.data
    });

    const deployedMerchantOverridesAPI = useDeployedMerchantOverrides(
        !!templateRepositoryId && templateRepositoryId > 0 && showMerchants,
        Number(version)
    );

    const { hasError, errorCmp } = errorController([
        singleDeploymentAPI.error,
        deployedMerchantOverridesAPI.error,
        deployedGlobalRulesAPI.error
    ]);

    if (
        singleDeploymentAPI.isLoading ||
        singleDeploymentAPI.isFetching ||
        deployedGlobalRulesAPI.isLoading ||
        deployedGlobalRulesAPI.isFetching ||
        (showMerchants && (deployedMerchantOverridesAPI.isLoading || deployedMerchantOverridesAPI.isFetching))
    ) {
        return <RouteLoading />;
    }
    if (hasError) return <>{errorCmp}</>;

    // Default or Templated Rules should not be seen in Execution Order view.
    const formatedRulesData = getRulesFormatForView(deployedGlobalRulesAPI.data?.rules!, isGlobalRuleSelectOption);

    return (
        <div>
            <MenuBar
                id='subNavBar'
                leftAlignedMenuItems={[
                    <div className={styles.openedFromText} key='openedFrom'>
                        {t('OPENED FROM REVISIONS')}
                    </div>
                ]}
            />
            <div className={styles.pageContainer}>
                <PageHeader>
                    <PageHeader.Left className={styles.titleText}>{t('versionNum', { version })}</PageHeader.Left>
                </PageHeader>
                <RevisionOverview data={singleDeploymentAPI.data} />
                <Tabs className={styles.revisionWindowTabs} startIndex={0} type='small'>
                    <TabContent headerTestId='rules-tab' hidden label={t('Global Rules')}>
                        <div className={styles.rulesContainer}>
                            <GlobalRulesOverview data={deployedGlobalRulesAPI.data!} readOnly />
                            <GlobalRulesTable
                                tableName='revision-window-global-rules'
                                rulesData={formatedRulesData}
                                columnNames={rulesColumnNames}
                                mode='revisionWindow'
                                groupByColumns={['virtualRuleGroup']}
                                // use revision and not deployed revision
                                deploymentVersion={Number(version)}
                                selectViewOption={viewOption}
                                setSelectViewOption={setViewOption}
                                tableInnerHeader={t('Rule Execution Order')}
                                options={{
                                    isRevision: true,
                                    isRevisionHistoryVisible: true
                                }}
                            />
                        </div>
                    </TabContent>
                    {showMerchants && (
                        <TabContent headerTestId='merchants-tab' hidden label={t('Merchants')}>
                            {showSingleMerchant ? (
                                <SingleMerchantView
                                    rulesData={deployedGlobalRulesAPI.data!}
                                    merchantId={currMerchant}
                                    version={Number(version)}
                                    setShowSingleMerchant={setShowSingleMerchant}
                                />
                            ) : (
                                <MerchantOverridesTable
                                    rawData={deployedMerchantOverridesAPI.data!}
                                    columnNames={merchantColumnNames}
                                    templateId={templateRepositoryId!}
                                    hideKebabMenu
                                    setShowSingleMerchant={setShowSingleMerchant}
                                    setCurrMerchant={setCurrMerchant}
                                    isLoading={false}
                                />
                            )}
                        </TabContent>
                    )}

                    {showTenantGroups && (
                        <TabContent headerTestId='tenantGroups-tab' hidden label={t('Tenant Groups')}>
                            {showSingleTenantGroup ? (
                                <SingleTenantGroupView
                                    rulesData={deployedGlobalRulesAPI.data!}
                                    version={Number(version)}
                                    currTenantGroup={currTenantGroup}
                                    setShowSingleTenantGroup={setShowSingleTenantGroup}
                                />
                            ) : (
                                <TenantGroupsTable
                                    mode='revisionWindow'
                                    deploymentData={singleDeploymentAPI.data!}
                                    columns={columns}
                                    setShowSingleTenantGroup={setShowSingleTenantGroup}
                                    setCurrTenantGroup={setCurrTenantGroup}
                                />
                            )}
                        </TabContent>
                    )}
                </Tabs>
            </div>
        </div>
    );
};

export default RevisionWindow;
